import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { LscVodCandidate } from '../types';
import { verifyLscVodCandidate, LscApiError } from '../api';
import { isLscVodDiagnosticsUiEnabled } from '../vodDiagnosticsFeatureFlag';
import ErrorBanner from './ErrorBanner';
import ScheduleMatchSelector from './ScheduleMatchSelector';
import {
  ADVANCED_TECHNICAL_DETAILS_HIDE,
  ADVANCED_TECHNICAL_DETAILS_LABEL,
  ADVANCED_TECHNICAL_DETAILS_SHOW,
  CHECK_SCOREBOARD_BUTTON_LABEL,
  CHECK_SCOREBOARD_ASYNC_PROGRESS_LABEL,
  CHECK_SCOREBOARD_HELPER_COPY,
  CHECK_SCOREBOARD_REVERIFY_LABEL,
  CHECK_SCOREBOARD_VERIFYING_LABEL,
  DETECTED_GAMERTAGS_LABEL,
  DETECTED_TEAMS_LABEL,
  GAME_VERIFICATION_STATUS_LABEL,
  GAME_VERIFICATION_TITLE,
  HOME_AWAY_ALIGNMENT_LABEL,
  MATCH_STRENGTH_LABEL,
  ROSTER_MISMATCH_LABEL,
} from '../commissionerReviewCopy';
import {
  TRUTH_VERIFICATION_COPY,
  FRAME_EVIDENCE_COLLAPSE_LABEL,
  FRAME_EVIDENCE_EXPAND_LABEL,
  truthAliasNotes,
  truthCanForceReverify,
  truthDebugMetadata,
  truthDetectedAwaySide,
  truthDetectedHomeSide,
  truthEvidenceSummary,
  truthFrameSummary,
  truthFrameVerificationBadgeStyle,
  truthFrameVerificationLabel,
  truthHomeAwayConfidence,
  truthFrameConsensus,
  truthFrameConsensusSummary,
  truthHasLowConfidenceFrameConsensus,
  truthHudTagNormalizationSummary,
  truthHudTagNormalizations,
  truthHudNormalizationStatusLabel,
  truthIdentityNotVerifiedMessage,
  truthLowConfidenceHudNormalizationMessage,
  truthIgnoredMenuNames,
  truthIgnoredOnFieldPlayerNames,
  truthIgnoredOverlayTags,
  truthIgnoredPlaycallOverlayNames,
  truthIdentityConflicts,
  truthLeagueMatchBadgeStyle,
  truthLeagueMatchLabel,
  truthLeagueResolutionSummary,
  truthRecommendedActionGuidance,
  truthResolutionNotes,
  truthScheduleBindingGuidance,
  truthScheduleBindingHeadline,
  truthScheduleBindingStatus,
  truthPrimaryScheduleMatches,
  truthFallbackScheduleMatches,
  truthAssignBlocked,
  truthShowsVerifyButton,
  truthStatusFromCandidate,
  truthTeamAbbreviations,
  truthTopHudGamerTags,
  truthVisionCostDebug,
  truthVisionSecondPass,
  truthVisionSecondPassSummary,
  truthHudCropPreprocessing,
  vodReviewScheduleMatchSelectionVisible,
} from '../vodTruth';
import {
  truthVerificationFailed,
  truthVerificationInProgress,
  truthVerificationProgressLabel,
} from '../vodTruthVerificationPolling';
import { useVodTruthVerificationPolling } from '../hooks/useVodTruthVerificationPolling';
import CommissionerIdentityOverridePanel from './CommissionerIdentityOverridePanel';
import CommissionerEmptyHudHandoffAttestationPanel from './CommissionerEmptyHudHandoffAttestationPanel';

interface TruthVerificationPanelProps {
  leagueId: string;
  candidate: LscVodCandidate;
  onUpdated: () => Promise<void>;
}

const TruthVerificationPanel: React.FC<TruthVerificationPanelProps> = ({
  leagueId,
  candidate,
  onUpdated,
}) => {
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFrameEvidence, setShowFrameEvidence] = useState(false);
  const [showAdvancedTechnical, setShowAdvancedTechnical] = useState(false);
  const verificationInProgress = truthVerificationInProgress(candidate);
  const verificationFailed = truthVerificationFailed(candidate);
  const { polling, pollError, startPolling, clearPollError } = useVodTruthVerificationPolling({
    candidate,
    onRefresh: onUpdated,
  });
  const isBusy = verifying || polling || verificationInProgress;
  const truthStatus = truthStatusFromCandidate(candidate);
  const evidenceSummary = truthEvidenceSummary(candidate);
  const activeGamerTags = truthTopHudGamerTags(candidate);
  const ignoredOverlayTags = truthIgnoredOverlayTags(candidate);
  const ignoredOnFieldNames = truthIgnoredOnFieldPlayerNames(candidate);
  const ignoredPlaycallNames = truthIgnoredPlaycallOverlayNames(candidate);
  const teamAbbreviations = truthTeamAbbreviations(candidate);
  const debugMetadata = truthDebugMetadata(candidate);
  const frameSummary = truthFrameSummary(candidate);
  const resolutionNotes = truthResolutionNotes(candidate);
  const aliasNotes = truthAliasNotes(candidate);
  const ignoredMenuNames = truthIgnoredMenuNames(candidate);
  const frameLabel = truthFrameVerificationLabel(candidate);
  const leagueLabel = truthLeagueMatchLabel(candidate);
  const leagueSummary = truthLeagueResolutionSummary(candidate);
  const recommendedGuidance = truthRecommendedActionGuidance(candidate);
  const detectedAway = truthDetectedAwaySide(candidate);
  const detectedHome = truthDetectedHomeSide(candidate);
  const homeAwayConfidence = truthHomeAwayConfidence(candidate);
  const scheduleBindingStatus = truthScheduleBindingStatus(candidate);
  const scheduleBindingHeadline = truthScheduleBindingHeadline(candidate);
  const scheduleBindingGuidance = truthScheduleBindingGuidance(candidate);
  const scheduleCandidateMatches = truthPrimaryScheduleMatches(candidate);
  const scheduleFallbackMatches = truthFallbackScheduleMatches(candidate);
  const identityConflicts = truthIdentityConflicts(candidate);
  const assignBlocked = truthAssignBlocked(candidate);
  const hudNormalizations = truthHudTagNormalizations(candidate);
  const hudNormalizationSummary = truthHudTagNormalizationSummary(candidate);
  const lowConfidenceHudMessage = truthLowConfidenceHudNormalizationMessage(candidate);
  const identityNotVerifiedMessage = truthIdentityNotVerifiedMessage(candidate);
  const frameConsensus = truthFrameConsensus(candidate);
  const frameConsensusSummary = truthFrameConsensusSummary(candidate);
  const lowConfidenceConsensus = truthHasLowConfidenceFrameConsensus(candidate);
  const visionSecondPass = truthVisionSecondPass(candidate);
  const visionSecondPassSummary = truthVisionSecondPassSummary(candidate);
  const visionCostDebug = truthVisionCostDebug(candidate);
  const hudCropPreprocessing = truthHudCropPreprocessing(candidate);

  const runVerify = async (force = false) => {
    setVerifying(true);
    setError(null);
    clearPollError();
    try {
      const result = await verifyLscVodCandidate(leagueId, candidate.id, { force });
      await onUpdated();
      if (result.async || result.truth_verification_status) {
        startPolling();
      }
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  if (!truthShowsVerifyButton(candidate)) {
    return null;
  }

  return (
    <div className="card space-y-4" id="frame-verification" data-testid="truth-verification-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-white">{GAME_VERIFICATION_TITLE}</h2>
      </div>

      <p className="text-sm text-secondary-400">{TRUTH_VERIFICATION_COPY}</p>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {pollError && (
        <ErrorBanner message={pollError} onDismiss={clearPollError} />
      )}

      {(isBusy || verificationFailed) && (
        <div
          className="flex items-start gap-3 rounded-lg border border-secondary-700 bg-secondary-900/60 px-4 py-3"
          data-testid="truth-verification-progress"
          role="status"
          aria-live="polite"
        >
          {isBusy ? (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary-400" />
          ) : null}
          <div className="space-y-1">
            <p className="text-sm text-secondary-200">
              {isBusy
                ? truthVerificationProgressLabel(candidate)
                : candidate.truth_scan_refused_message
                  || candidate.truth_verification_progress_label
                  || 'Verification did not complete.'}
            </p>
            {isBusy && (
              <p className="text-xs text-secondary-500">{CHECK_SCOREBOARD_ASYNC_PROGRESS_LABEL}</p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="btn-primary"
            disabled={isBusy}
            onClick={() => runVerify(false)}
            data-testid="verify-with-frames-button"
          >
            {isBusy ? CHECK_SCOREBOARD_VERIFYING_LABEL : CHECK_SCOREBOARD_BUTTON_LABEL}
          </button>
          {truthCanForceReverify(candidate) && (
            <button
              type="button"
              className="btn-secondary"
              disabled={isBusy}
              onClick={() => runVerify(true)}
            >
              {CHECK_SCOREBOARD_REVERIFY_LABEL}
            </button>
          )}
        </div>
        <p className="text-xs text-secondary-500" data-testid="check-scoreboard-helper">
          {CHECK_SCOREBOARD_HELPER_COPY}
        </p>
      </div>

      {candidate.truth_checked_at && (
        <>
          <div
            className="grid gap-4 border-t border-secondary-700 pt-4 sm:grid-cols-2"
            data-testid="truth-verification-sections"
          >
            <div className="space-y-2">
              <p className="text-sm text-secondary-500">{GAME_VERIFICATION_STATUS_LABEL}</p>
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${truthFrameVerificationBadgeStyle(candidate)}`}
                data-testid="truth-frame-verification-badge"
              >
                {frameLabel}
              </span>
            </div>
            {!candidate.commissioner_publish && (
            <div className="space-y-2">
              <p className="text-sm text-secondary-500">League match</p>
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${truthLeagueMatchBadgeStyle(candidate)}`}
                data-testid="truth-league-match-badge"
              >
                {leagueLabel}
              </span>
            </div>
            )}
          </div>

          {recommendedGuidance && !candidate.commissioner_publish && (
            <p
              className="text-sm text-secondary-300 border-l-2 border-primary-600 pl-3"
              data-testid="truth-recommended-action"
            >
              {recommendedGuidance}
            </p>
          )}

          {leagueSummary && !candidate.commissioner_publish && (
            <p className="text-sm text-secondary-400" data-testid="truth-league-resolution-summary">
              {leagueSummary}
            </p>
          )}

          <CommissionerIdentityOverridePanel
            leagueId={leagueId}
            candidate={candidate}
            onUpdated={onUpdated}
          />

          <CommissionerEmptyHudHandoffAttestationPanel
            leagueId={leagueId}
            candidate={candidate}
            onUpdated={onUpdated}
          />

          <dl
            className="grid gap-3 text-sm sm:grid-cols-2 border-t border-secondary-700 pt-4"
            data-testid="truth-commissioner-evidence-default"
          >
            <div>
              <dt className="text-secondary-500">{DETECTED_TEAMS_LABEL}</dt>
              <dd className="text-secondary-200" data-testid="truth-detected-teams">
                {(candidate.truth_detected_teams ?? []).join(', ') || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">{DETECTED_GAMERTAGS_LABEL}</dt>
              <dd className="text-secondary-200" data-testid="truth-active-gamer-tags-default">
                {activeGamerTags.join(', ') || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">{MATCH_STRENGTH_LABEL}</dt>
              <dd className="text-secondary-200 capitalize" data-testid="truth-confidence-default">
                {candidate.truth_confidence ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">{HOME_AWAY_ALIGNMENT_LABEL}</dt>
              <dd className="text-secondary-200 capitalize" data-testid="truth-home-away-confidence-default">
                {homeAwayConfidence ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Away team (scorebug left)</dt>
              <dd className="text-secondary-200" data-testid="truth-detected-away-default">
                {detectedAway?.team_name || detectedAway?.team_abbreviation || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Home team (scorebug right)</dt>
              <dd className="text-secondary-200" data-testid="truth-detected-home-default">
                {detectedHome?.team_name || detectedHome?.team_abbreviation || '—'}
              </dd>
            </div>
            {identityConflicts.length > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-secondary-500">{ROSTER_MISMATCH_LABEL}</dt>
                <dd className="text-secondary-300 space-y-1" data-testid="truth-identity-conflicts-default">
                  {identityConflicts.map((conflict, index) => (
                    <p key={`identity-conflict-default-${index}`}>
                      {conflict.tag ?? 'Tag'} detected for {conflict.detected_side ?? 'team'}
                      {conflict.current_league_assigned_member
                        ? `, but ${conflict.current_league_assigned_member} is assigned in this league.`
                        : ' without a matching league roster owner.'}
                    </p>
                  ))}
                </dd>
              </div>
            )}
          </dl>

          <div className="border-t border-secondary-700 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowFrameEvidence((open) => !open)}
              aria-expanded={showFrameEvidence}
              data-testid="frame-evidence-toggle"
            >
              {showFrameEvidence ? FRAME_EVIDENCE_COLLAPSE_LABEL : FRAME_EVIDENCE_EXPAND_LABEL}
            </button>
          </div>

          {showFrameEvidence && (
            <dl
              className="grid gap-3 text-sm sm:grid-cols-2 border-t border-secondary-700 pt-4"
              data-testid="truth-frame-evidence-details"
            >
              {frameSummary && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Scoreboard sample summary</dt>
                  <dd className="text-secondary-200" data-testid="truth-frame-summary">
                    Usable scoreboard samples: {frameSummary.usable} of {frameSummary.total}
                    {frameSummary.ignored > 0
                      ? ` · Ignored menu screens: ${frameSummary.ignored}`
                      : ''}
                  </dd>
                </div>
              )}
              {hudNormalizationSummary && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Roster-aware tag match</dt>
                  <dd
                    className="text-green-200/90 whitespace-pre-line"
                    data-testid="truth-hud-normalization-summary"
                  >
                    {hudNormalizationSummary.replace('Roster-aware tag match\n', '')}
                  </dd>
                </div>
              )}
              {hudNormalizations.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Gamertag normalization</dt>
                  <dd className="text-secondary-300 space-y-1" data-testid="truth-hud-normalizations">
                    {hudNormalizations.map((item, index) => (
                      <p key={`hud-normalization-${index}`}>
                        {item.raw_tag ?? 'Tag'}
                        {item.normalized_tag
                          ? ` → ${item.normalized_tag}`
                          : item.expected_identity
                            ? ` ≈ ${item.expected_identity}`
                            : ''}
                        {item.detected_side ? ` (${item.detected_side})` : ''}
                        {(() => {
                          const statusLabel = truthHudNormalizationStatusLabel(item);
                          return statusLabel ? ` · ${statusLabel}` : '';
                        })()}
                      </p>
                    ))}
                  </dd>
                </div>
              )}
              {lowConfidenceHudMessage && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Gamer tag needs review</dt>
                  <dd
                    className="text-amber-200/90 whitespace-pre-line"
                    data-testid="truth-hud-low-confidence-warning"
                  >
                    {lowConfidenceHudMessage.replace('Gamer tag needs review\n', '')}
                  </dd>
                </div>
              )}
              {identityNotVerifiedMessage && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Identity not verified</dt>
                  <dd
                    className="text-amber-200/90 whitespace-pre-line"
                    data-testid="truth-identity-not-verified"
                  >
                    {identityNotVerifiedMessage.replace('Identity not verified\n', '')}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-secondary-500">Ignored on-field/player names</dt>
                <dd className="text-secondary-200" data-testid="truth-ignored-on-field-names">
                  {ignoredOnFieldNames.join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-secondary-500">Ignored playcall overlay names</dt>
                <dd className="text-secondary-200" data-testid="truth-ignored-playcall-names">
                  {ignoredPlaycallNames.join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-secondary-500">Ignored menu/ticker/profile text</dt>
                <dd className="text-secondary-200" data-testid="truth-ignored-overlay-tags">
                  {[...ignoredMenuNames, ...ignoredOverlayTags.filter((t) => !ignoredMenuNames.includes(t))]
                    .join(', ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-secondary-500">Team abbreviations</dt>
                <dd className="text-secondary-200" data-testid="truth-team-abbreviations">
                  {teamAbbreviations.join(', ') || '—'}
                </dd>
              </div>
              {evidenceSummary && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">What LSC found</dt>
                  <dd className="text-secondary-300" data-testid="truth-evidence-summary">
                    {evidenceSummary}
                  </dd>
                </div>
              )}
              {resolutionNotes.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Resolution notes</dt>
                  <dd className="text-secondary-300" data-testid="truth-resolution-notes">
                    {resolutionNotes.join(' ')}
                  </dd>
                </div>
              )}
              {aliasNotes.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Alias resolution</dt>
                  <dd className="text-secondary-300" data-testid="truth-alias-notes">
                    {aliasNotes.join(' · ')}
                  </dd>
                </div>
              )}
            </dl>
          )}

          {isLscVodDiagnosticsUiEnabled() && (
          <div className="border-t border-secondary-700 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowAdvancedTechnical((open) => !open)}
              aria-expanded={showAdvancedTechnical}
              data-testid="advanced-technical-toggle"
            >
              {showAdvancedTechnical
                ? `${ADVANCED_TECHNICAL_DETAILS_HIDE} ${ADVANCED_TECHNICAL_DETAILS_LABEL}`
                : `${ADVANCED_TECHNICAL_DETAILS_SHOW} ${ADVANCED_TECHNICAL_DETAILS_LABEL}`}
            </button>
          </div>
          )}

          {isLscVodDiagnosticsUiEnabled() && showAdvancedTechnical && (
            <dl
              className="grid gap-3 text-sm sm:grid-cols-2 border-t border-secondary-700 pt-4"
              data-testid="truth-advanced-technical-details"
            >
              {frameConsensusSummary && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Multi-frame HUD consensus</dt>
                  <dd
                    className="text-secondary-300 whitespace-pre-line"
                    data-testid="truth-frame-consensus-summary"
                  >
                    {frameConsensusSummary.replace('Multi-frame HUD consensus\n', '')}
                  </dd>
                </div>
              )}
              {frameConsensus && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Per-frame HUD reads</dt>
                  <dd className="text-secondary-300 space-y-1" data-testid="truth-frame-consensus-reads">
                    {(frameConsensus.left_hud_candidates ?? []).map((item, index) => (
                      <p key={`left-read-${index}`}>
                        Left @ {item.offset_seconds ?? item.frame_index}s: {item.raw_tag ?? '—'}
                        {item.confidence ? ` · ${item.confidence}` : ''}
                      </p>
                    ))}
                    {(frameConsensus.right_hud_candidates ?? []).map((item, index) => (
                      <p key={`right-read-${index}`}>
                        Right @ {item.offset_seconds ?? item.frame_index}s: {item.raw_tag ?? '—'}
                        {item.confidence ? ` · ${item.confidence}` : ''}
                      </p>
                    ))}
                  </dd>
                </div>
              )}
              {lowConfidenceConsensus && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">HUD consensus needs review</dt>
                  <dd className="text-amber-200/90" data-testid="truth-frame-consensus-warning">
                    One or both HUD sides lack a safe multi-frame consensus. Publish remains blocked
                    until identity is verified.
                  </dd>
                </div>
              )}
              {visionSecondPass?.ran === true && visionSecondPassSummary && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Roster-constrained Vision recheck</dt>
                  <dd
                    className="text-secondary-300 whitespace-pre-line"
                    data-testid="truth-second-pass-summary"
                  >
                    {visionSecondPassSummary.replace('Roster-constrained Vision recheck\n', '')}
                  </dd>
                </div>
              )}
              {visionSecondPass?.ran === true && Array.isArray(visionSecondPass.results) && (
                <div className="sm:col-span-2">
                  <dt className="text-secondary-500">Second-pass HUD decisions</dt>
                  <dd className="text-secondary-300 space-y-1" data-testid="truth-second-pass-results">
                    {visionSecondPass.results.map((item, index) => (
                      <p key={`second-pass-${index}`}>
                        {String(item.side ?? 'side').toUpperCase()}: {String(item.decision ?? '—')}
                        {item.confidence ? ` · ${item.confidence}` : ''}
                        {item.visible_text ? ` · "${item.visible_text}"` : ''}
                      </p>
                    ))}
                  </dd>
                </div>
              )}
              {debugMetadata && (
                <>
                  <div>
                    <dt className="text-secondary-500">Sampled timestamps</dt>
                    <dd className="text-secondary-300">
                      {debugMetadata.sampledTimestamps.map((value) => `${value}s`).join(', ') || '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-secondary-500">Crop regions analyzed</dt>
                    <dd className="text-secondary-300">
                      {debugMetadata.cropRegions.join(', ') || '—'}
                    </dd>
                  </div>
                  {debugMetadata.visionModel && (
                    <div>
                      <dt className="text-secondary-500">Vision model</dt>
                      <dd className="text-secondary-300">{debugMetadata.visionModel}</dd>
                    </div>
                  )}
                  {debugMetadata.visionDetail && (
                    <div>
                      <dt className="text-secondary-500">Vision detail</dt>
                      <dd className="text-secondary-300">{debugMetadata.visionDetail}</dd>
                    </div>
                  )}
                  {visionCostDebug && (
                    <>
                      <div>
                        <dt className="text-secondary-500">Vision calls</dt>
                        <dd className="text-secondary-300" data-testid="truth-vision-calls">
                          primary {String(visionCostDebug.primary_vision_calls ?? 0)}, fallback{' '}
                          {String(visionCostDebug.fallback_vision_calls ?? 0)}, second-pass{' '}
                          {String(visionCostDebug.second_pass_vision_calls ?? 0)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-secondary-500">Estimated cost</dt>
                        <dd className="text-secondary-300" data-testid="truth-estimated-cost">
                          ${Number(visionCostDebug.truth_cost_estimate ?? 0).toFixed(3)}
                        </dd>
                      </div>
                      {hudCropPreprocessing.length > 0 && (
                        <div className="sm:col-span-2">
                          <dt className="text-secondary-500">HUD preprocessing</dt>
                          <dd className="text-secondary-300" data-testid="truth-hud-preprocessing">
                            {hudCropPreprocessing.join(', ')}
                          </dd>
                        </div>
                      )}
                    </>
                  )}
                  {debugMetadata.frameAnalysis.length > 0 && (
                    <div className="sm:col-span-2" data-testid="truth-debug-metadata">
                      <dt className="text-secondary-500">Frame analysis</dt>
                      <dd className="text-secondary-300">
                        {debugMetadata.frameAnalysis.map((frame, index) => {
                          const width = frame.frame_width ?? '—';
                          const height = frame.frame_height ?? '—';
                          const crops = Array.isArray(frame.crop_labels)
                            ? frame.crop_labels.join(', ')
                            : '—';
                          return (
                            <p key={`frame-debug-${index}`}>
                              {`${String(frame.offset_seconds ?? '—')}s · ${String(width)}x${String(height)} · ${crops}`}
                            </p>
                          );
                        })}
                      </dd>
                    </div>
                  )}
                </>
              )}
            </dl>
          )}

          {scheduleBindingStatus && scheduleBindingStatus !== 'not_applicable' && !assignBlocked && (
            <div
              className="space-y-3 border-t border-secondary-700 pt-4"
              id={vodReviewScheduleMatchSelectionVisible(candidate) ? 'match-selection' : undefined}
              data-testid="truth-schedule-binding"
            >
              <h3 className="text-sm font-medium text-white">Schedule binding</h3>
              {scheduleBindingHeadline && !candidate.commissioner_publish && (
                <p className="text-sm text-secondary-200" data-testid="truth-schedule-binding-headline">
                  {scheduleBindingHeadline}
                </p>
              )}
              {scheduleBindingGuidance && !candidate.commissioner_publish && (
                <p className="text-sm text-secondary-400" data-testid="truth-schedule-binding-guidance">
                  {scheduleBindingGuidance}
                </p>
              )}
              {(scheduleBindingStatus === 'ambiguous' ||
                (scheduleBindingStatus === 'bound' && scheduleCandidateMatches.length > 0)) && (
                <ScheduleMatchSelector
                  leagueId={leagueId}
                  candidate={candidate}
                  matches={scheduleCandidateMatches}
                  fallbackMatches={scheduleFallbackMatches}
                  boundMatchId={candidate.truth_bound_match_id}
                  onAssigned={onUpdated}
                />
              )}
            </div>
          )}
        </>
      )}

      {truthStatus === 'inconclusive' && (
        <p className="text-sm text-secondary-500">
          Verification was inconclusive. Use manual assign below to link this VOD to a match.
        </p>
      )}
    </div>
  );
};

export default TruthVerificationPanel;
